import DataTable from "../../components/common/DataTable";
import React from "react";
import { Badge } from "@mantine/core";
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { notifications } from "@mantine/notifications";
import { AnalyticsPageLayout, StatCard, ChartCard, TOOLTIP_STYLE, chartProps } from "./AnalyticsLayout";
import { Radio } from "lucide-react";

const liveEvents = [
  { key: "1", eventName: "Live Sports - Football Championship", streamer: "Sports Channel", viewers: 125430, duration: "3h 24m", bitrate: "4500 kbps", quality: "1080p", status: "active" },
  { key: "2", eventName: "Live Music Concert - Jazz Night", streamer: "Music Studio", viewers: 89320, duration: "2h 15m", bitrate: "6000 kbps", quality: "4K", status: "active" },
  { key: "3", eventName: "Live Gaming - Esports Tournament", streamer: "Gaming Pro", viewers: 156800, duration: "5h 42m", bitrate: "8000 kbps", quality: "1080p", status: "active" },
  { key: "4", eventName: "Cooking Show - Celebrity Chef", streamer: "Cooking Channel", viewers: 42150, duration: "1h 30m", bitrate: "3000 kbps", quality: "720p", status: "active" },
  { key: "5", eventName: "Talk Show - Celebrity Interview", streamer: "Entertainment", viewers: 0, duration: "0m", bitrate: "0 kbps", quality: "N/A", status: "ended" },
];

const liveColumns = [
  { title: "Event Name", dataIndex: "eventName", key: "eventName" },
  { title: "Streamer", dataIndex: "streamer", key: "streamer" },
  { title: "Current Viewers", dataIndex: "viewers", key: "viewers", render: (t) => t.toLocaleString() },
  { title: "Duration", dataIndex: "duration", key: "duration" },
  { title: "Bitrate", dataIndex: "bitrate", key: "bitrate" },
  { title: "Quality", dataIndex: "quality", key: "quality" },
  { title: "Status", dataIndex: "status", key: "status", render: (s) => s === "active" ? <Badge color="green" variant="light" size="sm">LIVE</Badge> : <Badge color="gray" variant="light" size="sm">Ended</Badge> },
];

const liveViewerData = [
  { time: "12:00 PM", viewers: 45000 },
  { time: "12:30 PM", viewers: 67000 },
  { time: "1:00 PM", viewers: 89000 },
  { time: "1:30 PM", viewers: 125000 },
  { time: "2:00 PM", viewers: 156000 },
  { time: "2:30 PM", viewers: 142000 },
];

const eventCategoryData = [
  { category: "Sports", events: 45, viewers: 520000 },
  { category: "Music", events: 32, viewers: 380000 },
  { category: "Gaming", events: 28, viewers: 650000 },
  { category: "Entertainment", events: 22, viewers: 290000 },
  { category: "Cooking", events: 15, viewers: 180000 },
];

const LiveStreamingAnalyticsPage = () => {
  const handleExport = (format) => {
    notifications.show({ message: `Exporting as ${format.toUpperCase()}...`, color: "green" });
  };

  return (
    <AnalyticsPageLayout
      icon={Radio}
      title="Live Streaming Analytics"
      subtitle="Real-time streaming metrics & viewer engagement"
      onExport={handleExport}
    >
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard title="Live Now" value={4} suffix="" />
        <StatCard title="Total Viewers" value={413700} suffix="" />
        <StatCard title="Avg Viewers/Event" value={82740} suffix="" />
        <StatCard title="Peak Bitrate" value={8000} suffix=" kbps" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <ChartCard title="Live Stream Viewers Over Time">
          <div style={{ height: 300 }}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={liveViewerData}>
                <defs>
                  <linearGradient id="liveViewers" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#ef4444" stopOpacity={0.4} />
                    <stop offset="100%" stopColor="#ef4444" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid {...chartProps.grid} />
                <XAxis dataKey="time" {...chartProps.axis} />
                <YAxis {...chartProps.axis} />
                <RechartsTooltip contentStyle={TOOLTIP_STYLE} />
                <Area type="monotone" dataKey="viewers" stroke="#ef4444" fill="url(#liveViewers)" strokeWidth={2} name="Viewers" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </ChartCard>
        <ChartCard title="Event Category Performance">
          <div style={{ height: 300 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={eventCategoryData}>
                <CartesianGrid {...chartProps.grid} />
                <XAxis dataKey="category" {...chartProps.axis} />
                <YAxis {...chartProps.axis} />
                <RechartsTooltip contentStyle={TOOLTIP_STYLE} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="events" fill="#3b82f6" name="Events" />
                <Bar dataKey="viewers" fill="#ef4444" name="Viewers" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </ChartCard>
      </div>

      <ChartCard title="Active & Recent Live Events">
        <DataTable dataSource={liveEvents} columns={liveColumns} pagination={false} scroll={{ x: 1200 }} rowKey="key" />
      </ChartCard>
    </AnalyticsPageLayout>
  );
};

export default LiveStreamingAnalyticsPage;
